import React, { useRef, useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { FiImage, FiX } from 'react-icons/fi';
import './style.scss';

import uploadApi from '_api/upload';

export default function EventDetails({
  formData = {},
  onBannersChange,
}) {
  const fileInputRef = useRef(null);
  const {
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext() || {};

  const formBanners = watch ? watch('banners') : formData.banners;
  const [banners, setBanners] = useState(formBanners || []);
  const [draggedIdx, setDraggedIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (formBanners) {
      setBanners(formBanners);
    }
  }, [formBanners]);

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setIsUploading(true);
    try {
      const newBanners = [];
      for (const file of files) {
        const res = await uploadApi.uploadPhoto([file]);
        const uploadedUrl = res?.msgResp?.url || res?.url;
        if (uploadedUrl) {
          newBanners.push({
            id: Math.random().toString(36).substring(2, 9),
            url: uploadedUrl,
          });
        }
      }
      const updated = [...banners, ...newBanners];
      setBanners(updated);
      if (setValue) setValue('banners', updated);
      if (onBannersChange) onBannersChange(updated);
    } catch (err) {
      console.error('Error uploading event banner:', err);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveBanner = (id, e) => {
    e.stopPropagation();
    const updated = banners.filter((item) => item.id !== id);
    setBanners(updated);
    if (setValue) setValue('banners', updated);
    if (onBannersChange) onBannersChange(updated);
  };

  const handleDragStart = (index) => {
    setDraggedIdx(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (dragOverIdx !== index) {
      setDragOverIdx(index);
    }
  };

  const handleDrop = (index) => {
    if (draggedIdx === null || draggedIdx === index) {
      setDraggedIdx(null);
      setDragOverIdx(null);
      return;
    }

    const updated = [...banners];
    const [draggedItem] = updated.splice(draggedIdx, 1);
    updated.splice(index, 0, draggedItem);

    setBanners(updated);
    if (setValue) setValue('banners', updated);
    if (onBannersChange) onBannersChange(updated);
    setDraggedIdx(null);
    setDragOverIdx(null);
  };

  return (
    <div className="form-card">
      <div className="form-card-title">Event details</div>

      <div className="form-group">
        <label className="form-label">Event banner</label>
        <input
          type="file"
          ref={fileInputRef}
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />

        {banners.length === 0 ? (
          <div
            className="banner-upload"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
          >
            <div className="banner-upload-icon">
              <FiImage size={18} />
            </div>
            <div className="banner-upload-text">{isUploading ? 'Uploading…' : 'Upload banner'}</div>
          </div>
        ) : (
          <div className="banner-grid">
            {banners.map((item, index) => (
              <div
                key={item.id}
                className={`banner-thumb ${
                  draggedIdx === index ? 'dragging' : ''
                } ${dragOverIdx === index ? 'drag-over' : ''}`}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={() => setDragOverIdx(null)}
                onDrop={() => handleDrop(index)}
              >
                <img src={item.url} alt={`Banner ${index + 1}`} />
                <span className="banner-thumb-order">{index + 1}</span>
                <button
                  type="button"
                  className="banner-thumb-remove"
                  onClick={(e) => handleRemoveBanner(item.id, e)}
                >
                  <FiX />
                </button>
              </div>
            ))}

            <div
              className="banner-add-tile"
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
            >
              <div className="banner-add-icon">
                <FiImage />
              </div>
              <div className="banner-add-text">{isUploading ? 'Uploading…' : 'Add another'}</div>
            </div>
          </div>
        )}

        <div className="banner-hint">
          Recommended 16:9 · PNG or JPG · drag thumbnails to reorder
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">
          Title <span className="req">*</span>
        </label>
        <input
          type="text"
          id="ev-title"
          placeholder="e.g. Fresh produce — May round"
          {...(register ? register('title') : {})}
        />
        {errors?.title && (
          <span style={{ color: '#E24B4A', fontSize: '11px', marginTop: '4px', display: 'block' }}>
            {errors.title.message}
          </span>
        )}
      </div>

      <div className="form-group">
        <label className="form-label">Description</label>
        <textarea
          id="ev-desc"
          placeholder="Describe your group buy event…"
          {...(register ? register('description') : {})}
        />
        {errors?.description && (
          <span style={{ color: '#E24B4A', fontSize: '11px', marginTop: '4px', display: 'block' }}>
            {errors.description.message}
          </span>
        )}
      </div>

      <div className="form-group">
        <label className="form-label">Pick-up location</label>
        <input
          type="text"
          id="ev-location"
          placeholder="e.g. Simei Street 1"
          {...(register ? register('location') : {})}
        />
        {errors?.location && (
          <span style={{ color: '#E24B4A', fontSize: '11px', marginTop: '4px', display: 'block' }}>
            {errors.location.message}
          </span>
        )}
      </div>

      <div className="date-row">
        <div className="form-group">
          <label className="form-label">
            Closing <span className="req">*</span>
          </label>
          <input
            type="date"
            id="ev-closing"
            {...(register ? register('closingDate') : {})}
          />
          {errors?.closingDate && (
            <span style={{ color: '#E24B4A', fontSize: '11px', marginTop: '4px', display: 'block' }}>
              {errors.closingDate.message}
            </span>
          )}
        </div>
        <div className="form-group">
          <label className="form-label">
            Collection <span className="req">*</span>
          </label>
          <input
            type="date"
            id="ev-collection"
            {...(register ? register('collectionDate') : {})}
          />
          {errors?.collectionDate && (
            <span style={{ color: '#E24B4A', fontSize: '11px', marginTop: '4px', display: 'block' }}>
              {errors.collectionDate.message}
            </span>
          )}
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Collection time</label>
        <div className="time-row">
          <input
            type="time"
            id="ev-time-from"
            {...(register ? register('timeFrom') : {})}
          />
          <span className="time-sep">to</span>
          <input
            type="time"
            id="ev-time-to"
            {...(register ? register('timeTo') : {})}
          />
        </div>
        {errors?.timeTo && (
          <span style={{ color: '#E24B4A', fontSize: '11px', marginTop: '4px', display: 'block' }}>
            {errors.timeTo.message}
          </span>
        )}
      </div>
    </div>
  );
}
